import React, { useState } from 'react';
import { Menu, Bell, Search, LogOut, ChevronDown, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Avatar from '../ui/Avatar';
import faviconImg from '../../favicon.png';

export default function Header({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const [showUser, setShowUser] = useState(false);

  return (
    <header style={{
      height: 64, background: '#fff', borderBottom: '1px solid #E2E8F0',
      display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12,
      position: 'sticky', top: 0, zIndex: 100, flexShrink: 0,
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
    }}>
      {/* Menu toggle */}
      <button onClick={onToggleSidebar} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 38, height: 38, borderRadius: 10,
        background: '#F1F5F9', border: 'none', cursor: 'pointer',
        color: '#64748B', transition: 'all 0.15s', flexShrink: 0,
      }}
        onMouseEnter={e => { e.currentTarget.style.background = '#E2E8F0'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#F1F5F9'; }}>
        <Menu size={18} />
      </button>

      {/* Search */}
      <div className="header-search" style={{
        flex: 1, maxWidth: 420, display: 'flex', alignItems: 'center', gap: 8,
        background: '#F8FAFC', borderRadius: 10, padding: '8px 14px',
        border: '1.5px solid #E2E8F0', transition: 'all 0.2s',
      }}>
        <Search size={15} color="#94A3B8" />
        <input placeholder="Поиск..." style={{
          border: 'none', background: 'transparent', outline: 'none',
          fontSize: 13, color: '#0F172A', width: '100%', fontFamily: 'inherit',
        }} />
      </div>

      <div style={{ flex: 1 }} />

      {/* Notifications */}
      <button style={{
        position: 'relative', width: 38, height: 38, borderRadius: 10,
        background: '#F8FAFC', border: '1.5px solid #E2E8F0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: '#64748B', transition: 'all 0.15s',
        flexShrink: 0,
      }}
        onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.borderColor = '#CBD5E1'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.borderColor = '#E2E8F0'; }}>
        <Bell size={17} />
        <span style={{
          position: 'absolute', top: 7, right: 7, width: 8, height: 8,
          background: '#EF4444', borderRadius: '50%', border: '2px solid #fff',
        }} />
      </button>

      {/* User dropdown */}
      <div style={{ position: 'relative' }}>
        <button onClick={() => setShowUser(v => !v)} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '6px 12px 6px 6px', borderRadius: 12,
          border: '1.5px solid #E2E8F0', background: '#fff',
          cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}>
          <Avatar name={user?.name || 'Admin'} size={30} />
          <div style={{ textAlign: 'left', display: 'none' }} className="user-info-text">
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', lineHeight: 1.2 }}>{user?.name}</div>
            <div style={{ fontSize: 11, color: '#94A3B8' }}>{
              user?.role === 'superadmin' ? 'Суперадмин' :
              user?.role === 'admin' ? 'Администратор' :
              user?.role === 'manager' ? 'Менеджер' : user?.role || ''
            }</div>
          </div>
          <ChevronDown size={14} color="#94A3B8" style={{ display: 'none' }} className="chevron-desktop" />
        </button>

        {/* Inline style for responsive text */}
        <style>{`
          @media (min-width: 640px) {
            .user-info-text { display: block !important; }
            .chevron-desktop { display: block !important; }
          }
          @media (max-width: 639px) {
            .header-search input::placeholder { font-size: 12px; }
          }
        `}</style>

        {showUser && (
          <>
            <div onClick={() => setShowUser(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 100,
              background: '#fff', borderRadius: 16,
              border: '1px solid #E2E8F0',
              boxShadow: '0 12px 36px rgba(0,0,0,0.1)', minWidth: 220, overflow: 'hidden',
              animation: 'slideUp 0.15s ease',
            }}>
              {/* Profile block */}
              <div style={{
                padding: '16px 18px', borderBottom: '1px solid #F1F5F9',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <img src={faviconImg} alt="PDA" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{user?.name}</div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>{user?.email}</div>
                </div>
              </div>
              <div style={{ padding: '6px' }}>
                <button style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '10px 12px', borderRadius: 10, border: 'none',
                  background: 'transparent', cursor: 'pointer', fontSize: 13,
                  color: '#475569', fontFamily: 'inherit', transition: 'all 0.12s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <User size={15} />
                  Профиль
                </button>
                <button onClick={() => { logout(); setShowUser(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '10px 12px', borderRadius: 10, border: 'none',
                  background: 'transparent', cursor: 'pointer', fontSize: 13,
                  color: '#EF4444', fontFamily: 'inherit', transition: 'all 0.12s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <LogOut size={15} />
                  Выйти
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
